import { Icon } from '@smartgift/frontend-toolkit';
import { useTranslation } from 'react-i18next';
import { createSearchParams, useNavigate } from 'react-router-dom';

import { createAutomationFormData } from '@hero/components/organisms/Automation/constants';
import { AUTOMATION_CREATION_STEPS } from '@hero/constants';
import { ROUTE_PATHS } from '@hero/routes';
import { useCreateAutomation } from '@hero/services';
import { useGetMerchant } from '@hero/services/merchant-service/hooks';
import { useNewAutomationStore, useNotification } from '@hero/store/hooks';
import { getNameAndTypeRule } from '@hero/validations';

function useCreateAutomationModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { updateType, updateName, updateCode, reset } = useNewAutomationStore();
  const { mutateAsync: createAutomation } = useCreateAutomation();
  const { data: merchantData } = useGetMerchant({ options: { includeConfigs: true } });

  const BIRTHDAY_CATEGORY_ID =
    merchantData?.configs?.['config-constants-dashboard']?.products?.categoryIds?.birthday || null;
  const WORK_ANNIVERSARY_CATEGORY_ID =
    merchantData?.configs?.['config-constants-dashboard']?.products?.categoryIds?.workAnniversary ||
    null;

  const showCreateAutomationModal = ({ ids }) => {
    showNotification({
      header: t('nameYourFeature', { name: t('automation').toLocaleLowerCase() }),
      formData: createAutomationFormData,
      primaryButtonProps: {
        endIcon: 'next',
        children: t('next')
      },
      secondaryButtonProps: { children: t('cancel') },
      notificationImage: <Icon name="automation" size="xxl" />,
      onSubmit: async ({ formValues, validate }) => {
        const isValid = await validate([
          {
            rule: getNameAndTypeRule(t('automation')),
            value: formValues
          }
        ]);
        if (!isValid) return true;

        reset();
        updateName(formValues.name);
        updateType(formValues.type);
        const { code } = await createAutomation({
          name: formValues.name,
          type: formValues.type,
          meta: { creationStep: AUTOMATION_CREATION_STEPS.newAutomationRules }
        });
        updateCode(code);
        navigate({
          pathname: ROUTE_PATHS.NEW_AUTOMATION_GIFTS,
          search:
            ids?.birthday || ids?.workAnniversary
              ? `?${createSearchParams({
                  categoryIds: formValues.type === 'birthday' ? ids.birthday : ids.workAnniversary
                })}`
              : ''
        });
      }
    });
  };

  return {
    showCreateAutomationModal: () =>
      showCreateAutomationModal({
        ids: { birthday: BIRTHDAY_CATEGORY_ID, workAnniversary: WORK_ANNIVERSARY_CATEGORY_ID }
      })
  };
}

export { useCreateAutomationModal };
